<template>
  <div class="col-sm-12">
    <!-- Filters Section -->
    <div class="card shadow-sm report-page">
      <div class="dropdown-container">
        <!-- Distributor -->
        <div class="dropdown-item">
          <label for="distributorSelect">Distributor</label>
          <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked || loading || pageLoading">
            <option value="">Select Distributor</option>
            <option v-if="!isDistributorUser" :value="ALL">All Distributors</option>

            <option v-for="distributor in distributorList" :key="distributor.profile_id"
              :value="String(distributor.profile_id)">
              {{ distributor.name }}
            </option>
          </select>
        </div>

        <!-- Date (single) -->
        <div class="dropdown-item">
          <label for="dateSelect">Date</label>
          <input id="dateSelect" type="date" v-model="selectedDate" :max="currentDate" @change="handleDateChange"
            @blur="validateSelectedDate(true)" :disabled="loading || pageLoading" />
        </div>

        <!-- Search & Export Button -->
        <div class="search-container">
          <button type="button" @click="performSearch(false)" class="btn button-default" :disabled="loading || pageLoading">
            <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
            {{ loading ? "Searching..." : "Search" }}
          </button>

          <button type="button" @click="exportToExcel" class="btn button-export" :disabled="!reportData.length || loading || pageLoading">
            Export to Excel
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="pageLoading || loading" class="loading-container mt-4">
      <div class="loading-card">
        <div class="loading-spinner-wrapper">
          <i class="fa fa-spinner fa-spin loading-icon"></i>
        </div>
        <h6 class="loading-title">
          <span v-if="pageLoading">Loading page...</span>
          <span v-else>Loading Daily Sales Monitoring Report...</span>
        </h6>
        <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
      </div>
    </div>

    <!-- ✅ Table -->
    <div v-if="!loading && !pageLoading && reportData.length && displayItems.length"
      class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <!-- Header row 1 -->
            <tr class="header-row-1">
              <th rowspan="2" class="align-middle text-center">FSM</th>
              <th rowspan="2" class="align-middle text-center">ASM</th>
              <th rowspan="2" class="align-middle text-center">Distributor</th>
              <th rowspan="2" class="align-middle text-center">Vehicle No</th>

              <!-- Item Group Headers (flat single row) -->
              <th
                v-for="it in displayItems"
                :key="'head_item_' + it.item_id"
                colspan="3"
                class="text-center item-group-head align-middle"
              >
                <span class="item-name-flat" :title="it.item_name">
                  {{ it.item_name }}
                </span>
              </th>

              <!-- Total Group Header -->
              <th colspan="3" class="text-center total-group-head align-middle">
                TOTAL
              </th>
            </tr>

            <!-- Header row 2 -->
            <tr class="header-row-2">
              <template v-for="it in displayItems" :key="'sub_' + it.item_id">
                <th class="text-center sub-col sub-load">Load</th>
                <th class="text-center sub-col sub-sale">Sale</th>
                <th class="text-center sub-col sub-bal item-section-end">Balance</th>
              </template>

              <th class="text-center sub-col sub-load">Load</th>
              <th class="text-center sub-col sub-sale">Sale</th>
              <th class="text-center sub-col sub-bal">Balance</th>
            </tr>
          </thead>

          <tbody>
            <!-- Real Report Rows -->
            <tr v-for="(row, i) in reportData" :key="row.dispatch_id ?? i">
              <td>{{ row.fsm_name || "-" }}</td>
              <td>{{ row.asm_name || "-" }}</td>
              <td>{{ row.distributor_name || "-" }}</td>
              <td>{{ vehicleNoOf(row.rep_id) || row.vehicle_no || "-" }}</td>

              <!-- Per Item: Load, Sale, Balance -->
              <template v-for="it in displayItems" :key="'val_' + it.item_id">
                <td class="text-end col-load">
                  {{ formatQty(row?.["loaded_" + it.item_id] ?? 0) }}
                </td>
                <td class="text-end col-sale">
                  {{ formatQty(row?.["sold_" + it.item_id] ?? 0) }}
                </td>
                <td class="text-end col-bal item-section-end">
                  {{ formatQty(row?.["bal_" + it.item_id] ?? 0) }}
                </td>
              </template>

              <!-- Row totals -->
              <td class="text-end fw-bold total-load">
                <strong>{{ formatQty(row.loaded_total ?? 0) }}</strong>
              </td>
              <td class="text-end fw-bold total-sale">
                <strong>{{ formatQty(row.sold_total ?? 0) }}</strong>
              </td>
              <td class="text-end fw-bold total-bal">
                <strong>{{ formatQty(row.bal_total ?? 0) }}</strong>
              </td>
            </tr>

            <!-- Grand Totals row -->
            <tr v-if="reportData.length" class="total-row">
              <td colspan="4" class="text-end"><strong>Total</strong></td>

              <template v-for="it in displayItems" :key="'tot_' + it.item_id">
                <td class="text-end">
                  <strong>{{ formatQty(columnTotal("loaded_", it.item_id)) }}</strong>
                </td>
                <td class="text-end">
                  <strong>{{ formatQty(columnTotal("sold_", it.item_id)) }}</strong>
                </td>
                <td class="text-end item-section-end">
                  <strong>{{ formatQty(columnTotal("bal_", it.item_id)) }}</strong>
                </td>
              </template>

              <td class="text-end">
                <strong>{{ formatQty(sumField("loaded_total")) }}</strong>
              </td>
              <td class="text-end">
                <strong>{{ formatQty(sumField("sold_total")) }}</strong>
              </td>
              <td class="text-end">
                <strong>{{ formatQty(sumField("bal_total")) }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ✅ No Dispatch Distributors Section (Cards) -->
    <div v-if="!loading && !pageLoading && showNoDispatchCards" class="no-dispatch-section mt-4">
      <div class="no-dispatch-header-bar">
        <div class="no-dispatch-title-box">
          <i class="fa fa-truck-loading me-2 text-warning"></i>
          <h5 class="mb-0 fw-bold">
            No Dispatch Distributors
            <span class="badge bg-danger ms-2">{{ noDispatchRows.length }}</span>
          </h5>
        </div>
        <div class="no-dispatch-hint text-muted">
          Distributors without dispatches on {{ selectedDate }}
        </div>
      </div>

      <div class="no-dispatch-cards-grid mt-3">
        <div
          v-for="(row, k) in noDispatchRows"
          :key="'nd_' + (row.distributor_profile_id || k)"
          class="no-dispatch-card-item"
        >
          <div class="card-distributor-header">
            <i class="fa fa-building-o me-2 text-primary"></i>
            <span class="distributor-title" :title="row.distributor_name">
              {{ row.distributor_name || "-" }}
            </span>
          </div>
          <div class="card-divider"></div>
          <div class="card-hierarchy-body">
            <div class="hierarchy-item">
              <span class="hierarchy-label">FSM:</span>
              <span class="hierarchy-value">{{ row.fsm_name || "-" }}</span>
            </div>
            <div class="hierarchy-item">
              <span class="hierarchy-label">ASM:</span>
              <span class="hierarchy-value">{{ row.asm_name || "-" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modern Empty State -->
    <div v-else-if="!loading && !pageLoading && searched && reportData.length === 0" class="no-data-container mt-4">
      <div class="no-data-card">
        <div class="no-data-icon-wrapper">
          <i class="fa fa-folder-open-o no-data-icon"></i>
        </div>
        <h5 class="no-data-title">No Data Found</h5>
        <p class="no-data-text">
          {{ searchErrorMessage || "There are no records matching your selected filters." }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  getDistributorList,
  fetchDailySalesMonitoringReport,
  fetchVehicleListrep,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "DailySalesMonitoringForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",
      distributorList: [],
      selectedDistributor: "",
      isDistributorLocked: false,
      selectedDate: currentDate,
      currentDate,
      pageLoading: false,
      loading: false,
      searched: false,
      searchErrorMessage: "",
      displayItems: [],
      reportData: [],
      vehicles: [],
      vehicleMap: {},
      distChainMap: {},
      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),
      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    // full columns count for colspan rows
    tableColspan() {
      return 4 + this.displayItems.length * 3 + 3;
    },
    showNoDispatchCards() {
      return this.noDispatchRows.length > 0 && (this.reportData.length > 0 || this.searched);
    },
    // noDispatchRows() {
    //   // show only when All Distributors (and not distributor user)
    //   if (this.isDistributorUser) return [];
    //   if (this.selectedDistributor !== this.ALL) return [];

    //   const list = Array.isArray(this.distributorList) ? this.distributorList : [];
    //   const rows = Array.isArray(this.reportData) ? this.reportData : [];

    //   const dispatchedSet = new Set(
    //     rows
    //       .map((r) => Number(r?.distributor_profile_id || 0))
    //       .filter(Boolean)
    //   );

    //   // safety: if backend not sending distributor_profile_id, don't show wrong results
    //   if (rows.length > 0 && dispatchedSet.size === 0) return [];

    //   const nameOf = (d) => {
    //     return (
    //       d?.name ??
    //       d?.business_name ??
    //       d?.full_name ??
    //       [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
    //       String(d?.profile_id ?? "")
    //     );
    //   };

    //   // helper: best-effort fsm/asm if API gives (otherwise '-')
    //   const fsmOf = (d) => d?.fsm_name ?? d?.fsm ?? d?.fsm_user_name ?? "";
    //   const asmOf = (d) => d?.asm_name ?? d?.asm ?? d?.asm_user_name ?? "";

    //   // create row objects with all totals 0 and cells missing => render as 0
    //   return list
    //     .filter((d) => !dispatchedSet.has(Number(d?.profile_id || 0)))
    //     .map((d) => {
    //       const out = {
    //         is_no_dispatch: true,
    //         distributor_profile_id: Number(d?.profile_id || 0),

    //         fsm_name: fsmOf(d),
    //         asm_name: asmOf(d),
    //         distributor_name: nameOf(d),

    //         rep_id: null,
    //         vehicle_no: "",

    //         loaded_total: 0,
    //         sold_total: 0,
    //         bal_total: 0,
    //       };

    //       // set visible item columns as 0 (optional but good for excel/export and clarity)
    //       this.itemsLoaded.forEach((it) => (out["loaded_" + it.item_id] = 0));
    //       this.itemsSold.forEach((it) => (out["sold_" + it.item_id] = 0));
    //       this.itemsBal.forEach((it) => (out["bal_" + it.item_id] = 0));

    //       return out;
    //     });
    // },

    //     noDispatchRows() {
    //   if (this.isDistributorUser) return [];
    //   if (this.selectedDistributor !== this.ALL) return [];

    //   const list = Array.isArray(this.distributorList) ? this.distributorList : [];
    //   const rows = Array.isArray(this.reportData) ? this.reportData : [];

    //   const dispatchedSet = new Set(
    //     rows.map((r) => Number(r?.distributor_profile_id || 0)).filter(Boolean)
    //   );

    //   if (rows.length > 0 && dispatchedSet.size === 0) return [];

    //   const nameOf = (d) =>
    //     String(
    //       d?.name ??
    //       d?.business_name ??
    //       d?.full_name ??
    //       [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
    //       d?.profile_id ??
    //       d?.id ??
    //       ""
    //     ).trim();

    //   const chainMap = this.distChainMap || {};

    //   return list
    //     .filter((d) => !dispatchedSet.has(Number(d?.profile_id || d?.id || 0)))
    //     .map((d) => {
    //       const distId = Number(d?.profile_id || d?.id || 0);
    //       const chain = chainMap[distId] || {};

    //       const fsm = String(chain?.fsm_name ?? d?.fsm_name ?? "").trim();
    //       const asm = String(chain?.asm_name ?? d?.asm_name ?? "").trim();

    //       const out = {
    //         is_no_dispatch: true,
    //         distributor_profile_id: distId,

    //         fsm_name: fsm,
    //         asm_name: asm,
    //         distributor_name: nameOf(d),

    //         rep_id: null,
    //         vehicle_no: "",

    //         loaded_total: 0,
    //         sold_total: 0,
    //         bal_total: 0,
    //       };

    //       this.itemsLoaded.forEach((it) => (out["loaded_" + it.item_id] = 0));
    //       this.itemsSold.forEach((it) => (out["sold_" + it.item_id] = 0));
    //       this.itemsBal.forEach((it) => (out["bal_" + it.item_id] = 0));

    //       return out;
    //     });
    // },

    noDispatchRows() {
      if (this.isDistributorUser) return [];
      if (this.selectedDistributor !== this.ALL) return [];

      const list = Array.isArray(this.distributorList) ? this.distributorList : [];
      const rows = Array.isArray(this.reportData) ? this.reportData : [];

      const dispatchedSet = new Set(
        rows.map((r) => Number(r?.distributor_profile_id || 0)).filter(Boolean)
      );

      if (rows.length > 0 && dispatchedSet.size === 0) return [];

      const nameOf = (d) =>
        String(
          d?.name ??
          d?.business_name ??
          d?.full_name ??
          [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
          d?.profile_id ??
          d?.id ??
          ""
        ).trim();

      const chainMap = this.distChainMap || {};

      return list
        .filter((d) => !dispatchedSet.has(Number(d?.profile_id || d?.id || 0)))
        .map((d) => {
          const distId = Number(d?.profile_id || d?.id || 0);
          const chain = chainMap[distId] || {};

          const fsm = String(chain?.fsm_name ?? d?.fsm_name ?? "").trim();
          const asm = String(chain?.asm_name ?? d?.asm_name ?? "").trim();

          return {
            distributor_profile_id: distId,
            fsm_name: fsm,
            asm_name: asm,
            distributor_name: nameOf(d),
          };
        })
        // ✅ SORT: FSM name A-Z (blank FSM goes last)
        .sort((a, b) => {
          const fa = String(a?.fsm_name ?? "").trim().toLowerCase();
          const fb = String(b?.fsm_name ?? "").trim().toLowerCase();

          if (!fa && !fb) return 0;
          if (!fa) return 1; // blank last
          if (!fb) return -1;

          // alphabetical
          if (fa < fb) return -1;
          if (fa > fb) return 1;

          // tie-breaker: ASM then Distributor
          const aa = String(a?.asm_name ?? "").trim().toLowerCase();
          const ab = String(b?.asm_name ?? "").trim().toLowerCase();
          if (aa < ab) return -1;
          if (aa > ab) return 1;

          const da = String(a?.distributor_name ?? "").trim().toLowerCase();
          const db = String(b?.distributor_name ?? "").trim().toLowerCase();
          if (da < db) return -1;
          if (da > db) return 1;

          return 0;
        });
    },




    showNoDispatchRows() {
      return this.reportData.length > 0 && this.noDispatchRows.length > 0;
    },
  },

  created() {
    this.initRoleFromStorage();
  },

  mounted() {
    this.initLoginAndLoad();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      const options = { position: "top-right", timeout: 3000 };
      if (type === "error") {
        t.error(message, options);
      } else if (type === "warning") {
        t.warning(message, options);
      } else if (type === "info") {
        t.info(message, options);
      } else {
        t.success(message, options);
      }
    },

    bodyOf(x) {
      return x?.data ?? x;
    },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },


    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();
      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      if (http === 401) return authMsg.test(msg);
      return http === 419 || http === 440 || http === 498 || authMsg.test(msg);
    },

    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    // ✅ active user filter
    isActiveUser(x) {
      const v = x?.is_active ?? x?.user_is_active ?? x?.app_user_is_active ?? x?.active;
      if (v === undefined || v === null) return true;
      return v === true || Number(v) === 1;
    },
    onlyActive(list) {
      return (Array.isArray(list) ? list : []).filter(this.isActiveUser);
    },

    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else {
        this.roleKey = "OTHER";
      }
    },

    async initLoginAndLoad() {
      this.pageLoading = true;
      await this.$nextTick(); // allow loader to paint

      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) return this.redirectToLogin("Session expired. Please login again.");
        this.token = token;

        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);

        this.initRoleFromStorage();

        await this.loadDistributorListOnly();
        if (this.redirecting) return;

        await this.loadVehiclesByRep();

        this.displayItems = [];
        this.reportData = [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.pageLoading = false;
      }

      // Automatically load report with default data
      if (this.selectedDistributor && this.selectedDate) {
        await this.performSearch(true);
      }
    },

    async loadVehiclesByRep() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchVehicleListrep(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
        this.vehicles = list;

        const m = {};
        for (const v of list) {
          const repId = Number(
            v?.rep_id ?? v?.sales_rep_id ?? v?.profile_id ?? v?.rep_profile_id ?? 0
          );

          const no = String(
            v?.vehicle_no ??
            v?.vehicle_number ??
            v?.registration_no ??
            v?.reg_no ??
            v?.vehicle ??
            v?.first_name ??
            ""
          );

          if (repId) m[repId] = no || "-";
        }
        this.vehicleMap = m;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
      }
    },

    vehicleNoOf(repId) {
      const id = Number(repId || 0);
      if (!id) return "";
      return this.vehicleMap[id] || "";
    },

    async loadDistributorListOnly() {
      try {
        const profileId = localStorage.getItem("profile_id");

        const res = await getDistributorList(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;

        let list = this.onlyActive(body?.data || []);

        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find((d) => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;
          }
        } else {
          this.isDistributorLocked = false;
          if (!this.selectedDistributor) {
            this.selectedDistributor = this.ALL;
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;
      if (dateStr.includes("/")) return false;
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;
      const y = Number(m[1]),
        mo = Number(m[2]),
        d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateSelectedDate(showToast = true) {
      if (!this.selectedDate) {
        if (showToast) this.toast("error", "Please select Date");
        return false;
      }
      if (!this.isValidISODate(this.selectedDate)) {
        if (showToast) this.toast("error", "Invalid Date");
        this.selectedDate = "";
        return false;
      }
      if (this.selectedDate > this.currentDate) {
        if (showToast) this.toast("error", "Date cannot be in the future");
        this.selectedDate = "";
        return false;
      }
      return true;
    },

    handleDateChange() {
      this.validateSelectedDate(false);
    },

    validateFilters() {
      if (!this.selectedDistributor) {
        this.toast("error", "Please select Distributor.");
        return false;
      }
      return this.validateSelectedDate(true);
    },

    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    columnTotal(prefix, itemId) {
      const key = prefix + String(itemId);
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[key]) || 0), 0);
    },

    sumField(field) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[field]) || 0), 0);
    },

    async performSearch(isInitial = false) {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFilters()) return;

      this.loading = true;
      this.searched = true;
      this.searchErrorMessage = "";
      this.displayItems = [];
      this.reportData = [];

      const toQty = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, "").trim());
        return Number.isFinite(n) ? n : 0;
      };

      const uiIsZero = (n) => Math.round(toQty(n)) === 0;

      const readQty = (row, prefix, id) => {
        const base = `${prefix}${id}`;
        if (row && row[base] !== undefined) return toQty(row[base]);

        if (row) {
          const k = Object.keys(row).find((x) => x.startsWith(base));
          if (k) return toQty(row[k]);
        }
        return 0;
      };

      try {
        const payload = {
          start_date: this.selectedDate,
          end_date: this.selectedDate,
          distributor_profile_id:
            this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
        };

        const apiRes = await fetchDailySalesMonitoringReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);
        if (this.handleUnauthorized(res)) return;

        if (!res || res.status !== true) {
          const msg = res?.message || "Error loading report.";
          this.searchErrorMessage = msg;
          this.toast("error", msg);
          return;
        }

        // ✅ build distributor -> ASM/FSM map from backend response
        this.distChainMap = {};
        const chain = Array.isArray(res.distributor_chain) ? res.distributor_chain : [];
        for (const c of chain) {
          const id = Number(c?.distributor_profile_id || 0);
          if (!id) continue;

          this.distChainMap[id] = {
            fsm_name: String(c?.fsm_name ?? "").trim(),
            asm_name: String(c?.asm_name ?? "").trim(),
          };
        }

        const rows = Array.isArray(res.data) ? res.data : [];
        if (!rows.length) {
          const apiMsg = res?.message || "";
          this.searchErrorMessage =
            apiMsg && !apiMsg.toLowerCase().includes("success")
              ? apiMsg
              : "No records found matching your selected filters.";
          if (!isInitial) {
            this.toast("error", "No data found for selected filters.");
          }
          return;
        }

        const rawLoaded = Array.isArray(res.items_loaded) ? res.items_loaded : [];
        const rawBal = Array.isArray(res.items_bal) ? res.items_bal : [];

        // Build unified parent items list (preserving sort order)
        const itemsMap = new Map();
        rawLoaded.forEach((x) => {
          const id = String(x.item_id);
          itemsMap.set(id, {
            item_id: id,
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          });
        });
        rawBal.forEach((x) => {
          const id = String(x.item_id);
          if (!itemsMap.has(id)) {
            itemsMap.set(id, {
              item_id: id,
              item_name: String(x.item_name ?? ""),
              sort_id: Number(x.sort_id ?? 0),
            });
          }
        });

        const allItems = Array.from(itemsMap.values()).sort(
          (a, b) => a.sort_id - b.sort_id
        );

        const mappedRows = rows.map((r) => {
          const out = { ...r };

          allItems.forEach((it) => {
            const loadQty = readQty(r, "loaded_", it.item_id);
            const balQty = readQty(r, "bal_", it.item_id);
            const directSold = readQty(r, "sold_", it.item_id);
            // In backend: bal = loaded - soldParent => soldParent = loaded - bal
            const soldQty = Math.max(0, directSold, loadQty - balQty);

            out["loaded_" + it.item_id] = loadQty;
            out["sold_" + it.item_id] = soldQty;
            out["bal_" + it.item_id] = balQty;
          });

          out.loaded_total = toQty(r?.loaded_total);
          out.sold_total = toQty(r?.sold_total);
          out.bal_total = toQty(r?.bal_total);
          out.rep_id = r?.rep_id ?? null;

          return out;
        });

        // Calculate totals for each item to show only items with non-zero activity
        const totals = {};
        allItems.forEach((it) => {
          totals[it.item_id] = { load: 0, sale: 0, bal: 0 };
        });

        mappedRows.forEach((r) => {
          allItems.forEach((it) => {
            totals[it.item_id].load += toQty(r["loaded_" + it.item_id]);
            totals[it.item_id].sale += toQty(r["sold_" + it.item_id]);
            totals[it.item_id].bal += toQty(r["bal_" + it.item_id]);
          });
        });

        this.displayItems = allItems.filter(
          (it) =>
            !uiIsZero(totals[it.item_id].load) ||
            !uiIsZero(totals[it.item_id].sale) ||
            !uiIsZero(totals[it.item_id].bal)
        );

        if (!this.displayItems.length) {
          this.searchErrorMessage = "All product totals are 0 for this date.";
          if (!isInitial) {
            this.toast("error", "All product totals are 0 for this date.");
          }
          return;
        }

        this.reportData = mappedRows;
        this.searchErrorMessage = "";
        this.toast("success", res?.message || `Report loaded successfully.`);
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        const msg = err?.response?.data?.message || "Error loading report.";
        this.searchErrorMessage = msg;
        this.toast("error", msg);
      } finally {
        this.loading = false;
      }
    },

    // ✅ Excel export includes "no dispatch rows" AFTER total (without affecting totals)
    exportToExcel() {
      if (!this.reportData.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const baseCols = ["FSM", "ASM", "Distributor", "Vehicle No"];

      // ---------------- FILTERS ----------------
      const filterRows = [
        ["Daily Sales Monitoring Report"],
        [],
        [
          "Distributor",
          this.selectedDistributor === this.ALL
            ? "All Distributors"
            : this.distributorList.find(
                (d) => String(d.profile_id) === String(this.selectedDistributor)
              )?.name || "-",
        ],
        ["Date", this.selectedDate],
        [],
      ];

      // ---------------- HEADERS ----------------
      const headerRow1 = [
        ...baseCols,
        ...this.displayItems.flatMap((it) => [it.item_name, "", ""]),
        "TOTAL",
        "",
        "",
      ];

      const headerRow2 = [
        "",
        "",
        "",
        "",
        ...this.displayItems.flatMap(() => ["Load", "Sale", "Balance"]),
        "Load",
        "Sale",
        "Balance",
      ];

      const fullCols = headerRow2.length;
      const padTo = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullCols) out.push("");
        return out.slice(0, fullCols);
      };

      // ---------------- DATA ROWS ----------------
      const dataRows = this.reportData.map((r) =>
        padTo([
          r.fsm_name || "-",
          r.asm_name || "-",
          r.distributor_name || "-",
          this.vehicleNoOf(r.rep_id) || r.vehicle_no || "-",

          ...this.displayItems.flatMap((it) => [
            Number(r["loaded_" + it.item_id]) || 0,
            Number(r["sold_" + it.item_id]) || 0,
            Number(r["bal_" + it.item_id]) || 0,
          ]),

          Number(r.loaded_total) || 0,
          Number(r.sold_total) || 0,
          Number(r.bal_total) || 0,
        ])
      );

      // ---------------- TOTAL ROW ----------------
      const totalRow = padTo([
        "TOTAL",
        "",
        "",
        "",
        ...this.displayItems.flatMap((it) => [
          this.columnTotal("loaded_", it.item_id),
          this.columnTotal("sold_", it.item_id),
          this.columnTotal("bal_", it.item_id),
        ]),
        this.sumField("loaded_total"),
        this.sumField("sold_total"),
        this.sumField("bal_total"),
      ]);

      // ---------------- BUILD AOA ----------------
      const aoa = [
        ...filterRows.map(padTo),
        padTo(headerRow1),
        padTo(headerRow2),
        ...dataRows,
        totalRow,
      ];

      // ---------------- NO-DISPATCH DISTRIBUTORS ----------------
      if (this.showNoDispatchCards) {
        aoa.push(Array(fullCols).fill("")); // spacer
        aoa.push(padTo([`NO DISPATCH DISTRIBUTORS (${this.noDispatchRows.length})`]));
        aoa.push(padTo(["FSM", "ASM", "Distributor"]));

        const ndRows = this.noDispatchRows.map((r) =>
          padTo([
            r.fsm_name || "-",
            r.asm_name || "-",
            r.distributor_name || "-",
          ])
        );

        aoa.push(...ndRows);
      }

      // ---------------- GENERATED INFO ----------------
      aoa.push(Array(fullCols).fill(""));
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";

      const now = new Date();
      const generatedAt = now.toLocaleString("en-US", { hour12: false });

      const generatedRows = [
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
      ];

      generatedRows.forEach((r) => aoa.push(padTo(r)));

      // ---------------- MERGES ----------------
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      const headerRowIndex = filterRows.length; // 0-indexed row for headerRow1
      const merges = [
        // base cols rowspan 2
        { s: { r: headerRowIndex, c: 0 }, e: { r: headerRowIndex + 1, c: 0 } },
        { s: { r: headerRowIndex, c: 1 }, e: { r: headerRowIndex + 1, c: 1 } },
        { s: { r: headerRowIndex, c: 2 }, e: { r: headerRowIndex + 1, c: 2 } },
        { s: { r: headerRowIndex, c: 3 }, e: { r: headerRowIndex + 1, c: 3 } },
      ];

      // Merge each item's 3 columns in headerRow1
      this.displayItems.forEach((_, i) => {
        const colStart = 4 + i * 3;
        merges.push({
          s: { r: headerRowIndex, c: colStart },
          e: { r: headerRowIndex, c: colStart + 2 },
        });
      });

      // Merge TOTAL's 3 columns in headerRow1
      const totalColStart = 4 + this.displayItems.length * 3;
      merges.push({
        s: { r: headerRowIndex, c: totalColStart },
        e: { r: headerRowIndex, c: totalColStart + 2 },
      });

      ws["!merges"] = merges;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily_Monitoring");
      XLSX.writeFile(wb, `Daily_Monitoring_${this.selectedDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>




<style scoped>
/* Filters Layout: Strictly Single Row */
.filters-card {
  border-radius: 8px;
  background-color: #ffffff;
}

.filter-row {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: flex-end !important;
  gap: 12px !important;
  width: 100% !important;
  overflow-x: auto;
  padding-bottom: 2px;
}

.filter-col {
  display: flex !important;
  flex-direction: column !important;
  flex: 0 0 auto !important;
  margin: 0 !important;
}

.filter-col-distributor {
  flex: 1 1 240px !important;
  min-width: 180px !important;
  max-width: 320px !important;
}

.filter-col-date {
  flex: 0 0 150px !important;
  width: 150px !important;
  min-width: 140px !important;
}

.filter-col-btn {
  flex: 0 0 auto !important;
  width: auto !important;
}

.filter-col label {
  display: block !important;
  margin-bottom: 6px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  color: #334155 !important;
  white-space: nowrap !important;
}

.filter-col select,
.filter-col input[type="date"] {
  width: 100% !important;
  height: 38px !important;
  padding: 6px 10px !important;
  border-radius: 6px !important;
  border: 1px solid #cbd5e1 !important;
  font-size: 13px !important;
  box-sizing: border-box !important;
  background-color: #ffffff !important;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.filter-col select:focus,
.filter-col input[type="date"]:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2) !important;
  outline: none !important;
}

.filter-col select:disabled,
.filter-col input[type="date"]:disabled {
  background-color: #f1f5f9 !important;
  cursor: not-allowed !important;
}

.btn-filter {
  height: 38px !important;
  padding: 0 18px !important;
  border: none !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
  width: auto !important;
  transition: all 0.2s ease !important;
}

.btn-filter-search {
  background: linear-gradient(45deg, #1e3a8a, #2563eb) !important;
  color: #ffffff !important;
}

.btn-filter-search:hover:not(:disabled) {
  background: linear-gradient(45deg, #172554, #1d4ed8) !important;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25) !important;
}

.btn-filter-export {
  background: linear-gradient(45deg, #15803d, #16a34a) !important;
  color: #ffffff !important;
}

.btn-filter-export:hover:not(:disabled) {
  background: linear-gradient(45deg, #166534, #15803d) !important;
  box-shadow: 0 2px 4px rgba(22, 163, 74, 0.25) !important;
}

.btn-filter:disabled {
  background: #94a3b8 !important;
  color: #ffffff !important;
  cursor: not-allowed !important;
  opacity: 0.65 !important;
  box-shadow: none !important;
}

/* =========================================================
   LOADING STATE (Matching Day End Report)
========================================================= */
.loading-container {
  width: 100%;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* =========================================================
   EMPTY STATE (Matching Day End Report)
========================================================= */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}

.card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th,
.table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  background-color: #ffffff;
}

.table-striped tbody tr:nth-of-type(odd) td {
  background-color: #f8f9fa;
}

.table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  z-index: 3;
}

.table thead .header-row-1 th {
  position: sticky;
  top: 0;
  z-index: 5;
}

.table thead .header-row-2 th {
  position: sticky;
  top: 46px;
  z-index: 4;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.total-row td {
  background: #f1f3f5 !important;
  font-weight: 700;
}

/* ✅ highlight row */
.no-dispatch-row td {
  background: #fff3cd !important;
  font-weight: 700;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.table thead th.item-group-head {
  background-color: #f1f5f9;
  border-bottom: 2px solid #cbd5e1;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap !important;
  padding: 8px 12px !important;
}

.item-name-flat {
  white-space: nowrap !important;
  font-size: 13px;
  font-weight: 700;
  display: inline-block;
}

.table thead th.total-group-head {
  background-color: #e2e8f0;
  border-bottom: 2px solid #94a3b8;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap !important;
  padding: 8px 12px !important;
}

.sub-col {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 8px !important;
}

.sub-load {
  background-color: #f8fafc;
  color: #2563eb;
}

.sub-sale {
  background-color: #f8fafc;
  color: #16a34a;
}

.sub-bal {
  background-color: #f8fafc;
  color: #475569;
}

.col-load {
  color: #1e293b;
}

.col-sale {
  color: #15803d;
  font-weight: 500;
}

.col-bal {
  color: #1e293b;
}

.item-section-end {
  border-right: 2px solid #cbd5e1 !important;
}

.total-load {
  background-color: #f8fafc !important;
  color: #1e40af;
}

.total-sale {
  background-color: #f8fafc !important;
  color: #166534;
}

.total-bal {
  background-color: #f8fafc !important;
  color: #1e293b;
}

/* =========================================================
   NO DISPATCH DISTRIBUTORS (CARDS VIEW)
========================================================= */
.no-dispatch-section {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}

.no-dispatch-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.no-dispatch-title-box {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.no-dispatch-title-box .badge {
  font-size: 13px;
  padding: 5px 10px;
  border-radius: 20px;
  background-color: #ef4444 !important;
}

.no-dispatch-hint {
  font-size: 13px;
  color: #64748b;
}

.no-dispatch-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.no-dispatch-card-item {
  background: #fffdf5;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 16px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.no-dispatch-card-item:hover {
  transform: translateY(-2px);
  border-color: #f59e0b;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
}

.card-distributor-header {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.card-distributor-header i {
  color: #f59e0b;
  font-size: 16px;
}

.distributor-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-divider {
  height: 1px;
  background: #fef3c7;
  margin-bottom: 12px;
}

.card-hierarchy-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hierarchy-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.hierarchy-label {
  font-weight: 600;
  color: #64748b;
}

.hierarchy-value {
  font-weight: 500;
  color: #334155;
  text-align: right;
}
</style>